import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, Mail, ArrowLeft, Zap, FileText, Scale, Sparkles } from "lucide-react";
import { getCheckoutStatus, getMolliePaymentStatus, getComparisonRecommendation, trackComparisonClick } from "@/lib/api";
import ProviderGrid from "@/components/ProviderGrid";
import { toast } from "sonner";

const PACKAGE_COPY = {
  report: {
    title: "Rapport ontgrendeld",
    description: "Je volledige EnergieKontract-rapport is nu beschikbaar.",
    icon: FileText,
  },
  bundle: {
    title: "Brievenbundel ontgrendeld",
    description: "Je rapport + alle 4 PDF brieven staan klaar voor je.",
    icon: FileText,
  },
  lawyer_intake: {
    title: "Intake geboekt",
    description: "Onze jurist neemt binnen 24 uur contact op voor je 30-min intake.",
    icon: Scale,
  },
};

export default function Success() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");          // Stripe
  const txnId = params.get("txn_id");                  // Mollie internal txn id
  const provider = params.get("provider");             // "mollie" | (default stripe)
  const analysisId = params.get("analysis_id");
  const packageId = params.get("package");

  const [state, setState] = useState("polling"); // polling | paid | failed | expired | pending
  const [recommendation, setRecommendation] = useState(null);

  // Once paid, fetch personalized provider recommendation for cross-sell
  useEffect(() => {
    if (state === "paid" && analysisId && !recommendation) {
      getComparisonRecommendation({ analysis_id: analysisId })
        .then(setRecommendation)
        .catch(() => {});
    }
  }, [state, analysisId, recommendation]);

  const handleProviderClick = async (providerId) => {
    try {
      const r = await trackComparisonClick({ provider_id: providerId, analysis_id: analysisId });
      if (r.url) window.open(r.url, "_blank", "noopener,noreferrer");
    } catch {
      toast.error("Doorsturen mislukt — probeer opnieuw.");
    }
  };

  useEffect(() => {
    const isMollie = provider === "mollie" || !!txnId;
    const id = isMollie ? txnId : sessionId;
    if (!id) {
      setState("failed");
      return;
    }
    let cancelled = false;
    let attemptsLocal = 0;
    const MAX_ATTEMPTS = 30;       // 30 × 3s = 90s max wait (iDEAL can take 30-60s for "paid")
    const POLL_INTERVAL_MS = 3000; // 3s — gentler on the API
    const MAX_ERROR_RETRIES = 6;

    const poll = async () => {
      if (cancelled) return;
      attemptsLocal += 1;
      try {
        const r = isMollie ? await getMolliePaymentStatus(id) : await getCheckoutStatus(id);
        const ps = r.payment_status;
        if (ps === "paid") {
          setState("paid");
          return;
        }
        // Real failures from Mollie/Stripe
        if (ps === "expired") {
          setState("expired");
          return;
        }
        if (ps === "canceled" || ps === "failed") {
          setState("failed");
          return;
        }
        // open | pending — keep polling
        if (attemptsLocal >= MAX_ATTEMPTS) {
          // Don't show "failed" — payment may still be processing at the bank.
          setState("pending");
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch {
        if (attemptsLocal < MAX_ERROR_RETRIES) {
          setTimeout(poll, POLL_INTERVAL_MS);
        } else {
          // Network/server hiccup — don't say "geen geld afgeschreven" because we don't actually know.
          setState("pending");
        }
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, txnId, provider]);

  const pkgCopy = PACKAGE_COPY[packageId] || PACKAGE_COPY.report;
  const PackageIcon = pkgCopy.icon;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="success-page">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-xl bg-blue-700 text-white grid place-items-center">
              <Zap className="h-5 w-5" />
            </div>
            <span className="font-heading font-bold text-lg text-slate-900">EnergieKontract</span>
          </Link>
          <Link to="/" className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-blue-700">
            <ArrowLeft className="h-4 w-4" />
            Home
          </Link>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 sm:px-8 py-20">
        <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center shadow-[0_24px_60px_-24px_rgba(15,23,42,0.18)]">
          {state === "polling" && (
            <>
              <div className="mx-auto h-16 w-16 rounded-full bg-blue-50 grid place-items-center">
                <Loader2 className="h-8 w-8 text-blue-700 animate-spin" />
              </div>
              <h1 className="mt-6 font-heading text-2xl font-bold text-slate-900" data-testid="success-polling-title">
                Betaling wordt verwerkt...
              </h1>
              <p className="mt-2 text-slate-600">
                Eventjes geduld — we bevestigen je betaling.
              </p>
            </>
          )}

          {state === "paid" && (
            <>
              <div className="mx-auto h-16 w-16 rounded-full bg-emerald-100 grid place-items-center">
                <CheckCircle2 className="h-9 w-9 text-emerald-700" />
              </div>
              <div className="mt-6 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-emerald-700">
                <PackageIcon className="h-3.5 w-3.5" />
                {pkgCopy.title}
              </div>
              <h1 className="mt-2 font-heading text-3xl font-bold text-slate-900" data-testid="success-paid-title">
                Bedankt! Je betaling is gelukt.
              </h1>
              <p className="mt-3 text-slate-600 max-w-md mx-auto">
                {pkgCopy.description}
              </p>
              <div className="mt-7 inline-flex items-center gap-2 text-sm text-slate-700 bg-emerald-50 border border-emerald-200 rounded-full px-4 py-2" data-testid="success-email-badge">
                <Mail className="h-4 w-4 text-emerald-700" />
                <span><strong>Check je inbox</strong> — we hebben alles als PDF gemaild</span>
              </div>
              <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                {analysisId && packageId !== "lawyer_intake" && (
                  <Link
                    to={`/rapport/${analysisId}?unlocked=1`}
                    className="inline-flex items-center justify-center gap-2 bg-blue-700 hover:bg-blue-800 text-white font-medium rounded-lg px-6 py-3"
                    data-testid="success-view-report-btn"
                  >
                    Bekijk je rapport online
                  </Link>
                )}
                <Link
                  to="/"
                  className="inline-flex items-center justify-center bg-white border border-slate-200 hover:border-blue-200 text-slate-700 font-medium rounded-lg px-6 py-3"
                  data-testid="success-home-btn"
                >
                  Nog een contract checken
                </Link>
              </div>
            </>
          )}

          {state === "pending" && (
            <>
              <div className="mx-auto h-16 w-16 rounded-full bg-amber-50 grid place-items-center">
                <Loader2 className="h-8 w-8 text-amber-600 animate-spin" />
              </div>
              <h1 className="mt-6 font-heading text-2xl font-bold text-slate-900" data-testid="success-pending-title">
                Betaling wordt nog verwerkt
              </h1>
              <p className="mt-3 text-slate-600 max-w-md mx-auto">
                Het duurt iets langer dan normaal om je iDEAL betaling te bevestigen. Vaak komt 'ie binnen 1-2 minuten alsnog door.
              </p>
              <p className="mt-2 text-sm text-slate-500 max-w-md mx-auto">
                Heb je het geld al van je rekening zien afschrijven? Geen zorgen — gebruik onderstaande knop om je rapport alsnog te ontgrendelen.
              </p>
              <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
                {analysisId && (
                  <Link
                    to={`/rapport/${analysisId}`}
                    className="inline-flex items-center justify-center bg-blue-700 hover:bg-blue-800 text-white font-medium rounded-lg px-6 py-3"
                    data-testid="success-pending-retry-btn"
                  >
                    Open mijn rapport
                  </Link>
                )}
                <button
                  onClick={() => window.location.reload()}
                  className="inline-flex items-center justify-center bg-white border border-slate-200 hover:border-blue-200 text-slate-700 font-medium rounded-lg px-6 py-3"
                  data-testid="success-pending-refresh-btn"
                >
                  Status opnieuw controleren
                </button>
              </div>
              <p className="mt-6 text-xs text-slate-400">
                Lukt het niet? Stuur een mail naar info@energiekontract.nl met je betaal-bewijs, we ontgrendelen 'm dan handmatig.
              </p>
            </>
          )}

          {(state === "failed" || state === "expired") && (
            <>
              <div className="mx-auto h-16 w-16 rounded-full bg-red-100 grid place-items-center">
                <XCircle className="h-8 w-8 text-red-600" />
              </div>
              <h1 className="mt-6 font-heading text-2xl font-bold text-slate-900" data-testid="success-failed-title">
                Betaling niet voltooid
              </h1>
              <p className="mt-2 text-slate-600">
                {state === "expired"
                  ? "De betaalsessie is verlopen. Probeer het opnieuw vanuit je rapport."
                  : "Er ging iets mis bij het bevestigen van de betaling. Geen geld is afgeschreven."}
              </p>
              <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
                {analysisId && (
                  <Link
                    to={`/rapport/${analysisId}`}
                    className="inline-flex items-center justify-center bg-blue-700 hover:bg-blue-800 text-white font-medium rounded-lg px-6 py-3"
                    data-testid="success-retry-btn"
                  >
                    Terug naar rapport
                  </Link>
                )}
                <Link
                  to="/"
                  className="inline-flex items-center justify-center bg-slate-900 hover:bg-slate-800 text-white font-medium rounded-lg px-6 py-3"
                  data-testid="success-failed-home-btn"
                >
                  Naar home
                </Link>
              </div>
            </>
          )}
        </div>

        {/* Cross-sell: AI top-3 providers after successful payment */}
        {state === "paid" && recommendation?.top?.length > 0 && (
          <section className="mt-10 bg-gradient-to-br from-blue-50 via-white to-emerald-50 border border-blue-100 rounded-2xl p-6 sm:p-8 shadow-[0_8px_30px_-12px_rgba(15,23,42,0.08)]" data-testid="success-crosssell">
            <div className="flex items-start gap-3 mb-5">
              <div className="h-11 w-11 rounded-xl bg-blue-700 text-white grid place-items-center flex-shrink-0">
                <Sparkles className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-700">Volgende stap</div>
                <h2 className="mt-1 font-heading text-xl sm:text-2xl font-bold text-slate-900 tracking-tight leading-tight">
                  Stap nu over en bespaar honderden euro's per jaar
                </h2>
                <p className="mt-2 text-sm text-slate-600">
                  Op basis van jouw contract hebben we de 3 beste alternatieven voor je geselecteerd. Eén klik en je regelt het direct.
                </p>
              </div>
            </div>
            <ProviderGrid
              top={recommendation.top}
              all={recommendation.all || []}
              onProviderClick={handleProviderClick}
              possibleSavings={0}
            />
          </section>
        )}
      </main>
    </div>
  );
}
